import React from 'react'
import {
  Flex,
  Row,
  Col,
  Radio,
  Empty,
  Divider,
  Card,
  Splitter,
  Affix,
  Typography,
} from 'antd'
import { useParams } from 'react-router'

import { requireAuthentication } from '../helpers/authentication'
import {
  useGlobalSearchContext,
  useGlobalSearchDispatch,
} from './GlobalSearchContext'
import { GlobalSearchQueryButton } from './GlobalSearchQueryButton'
import { useDispatch } from 'react-redux'
import { setVIN, resetVIN } from '../actions/app-actions'
import { Image } from '../FunctionalDiagrams/Image'
import { useVisualAids } from '../hooks/VisualAids'
import Loading from '../components/Loading'
import './visual-aids-style.scss'

export const VisualAidImage = ({
  fileName,
  showControls = true,
  panning = { disabled: false },
  zoomPanPinchDisabled = false,
  clickable = true,
  onClick,
  extraOverlay,
  dataTestId = null,
}) => {
  const globalSearchContext = useGlobalSearchContext()

  const deselectAll = ({ svg }) => {
    const components = svg.querySelectorAll('g[id^="component_"]')
    components.forEach((group) => {
      const rect = group.querySelector('rect')
      rect.style.fill = 'transparent'
    })
  }

  const deselect = ({ svg, componentId }) => {
    const group = svg.getElementById(`component_${componentId}`)
    const rect = group.querySelector('rect')
    rect.style.fill = 'transparent'
  }

  const onClickHandler = (event) => {
    const svg = event.currentTarget
    const target = event.target

    deselectAll({ svg })

    if (target && target.parentNode.id.startsWith('component_')) {
      highlight({ rect: target })
      onClick({ componentId: target.parentNode.id.replace('component_', '') })
    }
  }

  const select = ({ svg, componentId }) => {
    const group = svg.getElementById(`component_${componentId}`)
    const rect = group.querySelector('rect')
    highlight({ rect })
  }

  const highlight = ({ rect }) => {
    rect.style.fill = 'rgb(1, 137, 233)'
    rect.style.fillOpacity = '0.5'
  }

  const svgPostProcess = ({ svgRef }) => {
    if (!clickable) {
      svgRef.current.style.pointerEvents = 'none'
      return
    }
    svgRef.current.setAttribute('data-testid', 'visual-aid-image')
    const visualAidGroup = svgRef.current.getElementById('visual_aid')
    const components = visualAidGroup.querySelectorAll('g[id^="component_"]')

    components.forEach((group) => {
      const rect = group.querySelector('rect')
      rect.style.cursor = 'pointer'
      rect.style.fill = 'transparent'
      group.setAttribute('data-testid', group.id.replace('component_', ''))
    })

    if (globalSearchContext.visualAid.componentId) {
      select({
        svg: svgRef.current,
        componentId: globalSearchContext.visualAid.componentId,
      })
    }
  }

  return (
    <Image
      key={`${fileName}`}
      fileName={fileName}
      svgPostProcess={svgPostProcess}
      showControls={showControls}
      clickHandler={onClickHandler}
      panning={panning}
      extraOverlay={extraOverlay}
      zoomPanPinchDisabled={zoomPanPinchDisabled}
      dataTestId={dataTestId}
    />
  )
}

function VisualAidPage({ intl }) {
  const { vin } = useParams()
  const dispatch = useDispatch()
  const globalSearchContext = useGlobalSearchContext()
  const globalSearchDispatch = useGlobalSearchDispatch()
  const { isLoading: visualAidsIsLoading, visualAids } = useVisualAids({
    vin,
  })

  React.useEffect(() => {
    dispatch(setVIN(vin))
    return () => dispatch(resetVIN())
  }, [vin, dispatch])

  return (
    <div id="visual-aids">
      <Splitter>
        <Splitter.Panel defaultSize="50%" min="20%" max="70%">
          <Row className="visual-aid-panel-header">
            <Col offset={1}>
              Component: {globalSearchContext.visualAid.componentId}
            </Col>
          </Row>
          <Divider size="small" />
          {globalSearchContext.visualAid.fileName ? (
            <div className="visual-aid-image-container">
              <VisualAidImage
                dataTestId="visual-aid-svg"
                onClick={({ componentId }) =>
                  globalSearchDispatch.handleVisalAidComponentSelection({
                    componentId,
                  })
                }
                fileName={globalSearchContext.visualAid.fileName}
                vin={vin}
                extraOverlay={
                  <Affix className="visual-aid-svg-overlay">
                    <GlobalSearchQueryButton
                      vin={vin}
                      query={globalSearchContext.visualAid.componentId}
                    />
                  </Affix>
                }
              />
            </div>
          ) : (
            <Row className="visual-aid-empty" justify="center" align="middle">
              <Col>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Select Visual Aid"
                />
              </Col>
            </Row>
          )}
        </Splitter.Panel>
        <Splitter.Panel>
          <Row className="visual-aid-panel-header">
            <Col offset={1}>
              Visual Aid: {globalSearchContext.visualAid.name}
            </Col>
          </Row>
          <Divider size="small" />
          {visualAidsIsLoading ? (
            <Loading />
          ) : visualAids.length > 0 ? (
            <Flex wrap justify="space-evenly" align="center" gap="middle">
              {visualAids.map((aid) => (
                <Card
                  key={aid.id}
                  title={
                    <Radio
                      checked={aid.id === globalSearchContext.visualAid.aidId}
                    >
                      <Typography.Text
                        ellipsis={{ tooltip: true }}
                        className="visual-aid-preview-card-title"
                      >
                        <span>{aid.name}</span>
                      </Typography.Text>
                    </Radio>
                  }
                  variant="borderless"
                  hoverable
                  onClick={() => {
                    globalSearchDispatch.visualAidReset()
                    globalSearchDispatch.handleVisualAidSelection({
                      aidId: aid.id,
                      fileName: aid.file_name,
                      name: aid.name,
                    })
                  }}
                  styles={{
                    body: {
                      height: '90%',
                    },
                  }}
                  className="visual-aid-preview-card"
                >
                  <div className="visual-aid-image-preview-container">
                    <VisualAidImage
                      showControls={false}
                      fileName={aid.file_name}
                      zoomPanPinchDisabled={true}
                      clickable={false}
                      dataTestId="visual-aid-svg-preview"
                    />
                  </div>
                </Card>
              ))}
            </Flex>
          ) : (
            <Row className="visual-aid-empty" justify="center" align="middle">
              <Col>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No Visual Aids Found"
                />
              </Col>
            </Row>
          )}
        </Splitter.Panel>
      </Splitter>
    </div>
  )
}

export default requireAuthentication(VisualAidPage)
